<!-- Full-page marble/sandstone background -->
<div class="min-h-screen flex items-center justify-center px-4 py-12"
	style="background-image: url('/images/marble_texture.png'); background-size: cover; background-position: center;">

	<!-- Outer card frame — sandstone border with subtle inset rule (matches LoginPageFrame) -->
	<div class="w-full max-w-2xl rounded-lg shadow-2xl overflow-hidden"
		style="border: 6px solid var(--color-sandstone); outline: 1px solid var(--color-gold-500);">

		<div class="flex">

			<!-- ── Left panel: leftLogin image with text overlay ── -->
			<div class="shrink-0 relative flex flex-col items-center px-5 text-center"
				style="width: 224px; background-image: url('/images/leftLogin.png'); background-size: cover; background-position: center bottom;">

				<!-- Top padding -->
				<div style="flex: 0 0 8%;"></div>

				<!-- Brand name — above the emblem -->
				<h1 style="font-family: var(--font-family-display); font-size: 1.125rem; font-weight: 600; letter-spacing: 0.12em; line-height: 1.3; color: white; text-shadow: 0 1px 4px rgba(0,0,0,0.6); margin-bottom: 0;">
					<span ro-i18n eikey="login.brandName">The Socrates Initiative</span>
				</h1>

				<!-- Spacer spanning the emblem -->
				<div style="flex: 0 0 42%;"></div>

				<!-- Gold rule -->
				<div style="width: 40px; border-top: 1px solid var(--color-gold-500); margin-bottom: 6px;"></div>

				<!-- Tagline -->
				<p style="font-family: var(--font-family-display); font-size: 0.6rem; font-weight: 500; letter-spacing: 0.18em; text-transform: uppercase; color: var(--color-gold-400); text-shadow: 0 1px 3px rgba(0,0,0,0.5); margin-bottom: 12px; line-height: 1.5;">
					<span ro-i18n eikey="login.brandTagline">Constructive Planning for Results</span>
				</p>

				<!-- Descriptor -->
				<p style="font-family: var(--font-family-ui); font-size: 0.7rem; line-height: 1.6; color: rgba(255,255,255,0.7); text-shadow: 0 1px 3px rgba(0,0,0,0.5);">
					<span ro-i18n eikey="login.brandDescriptor">A structured platform for deliberate collective reasoning.</span>
				</p>

				<!-- Bottom spacer -->
				<div style="flex: 1;"></div>

			</div>

			<!-- ── Right panel: marble form ── -->
			<div class="flex flex-1 flex-col"
				style="background-image: url('/images/marble_texture.png'); background-size: cover; background-position: center;">

				<!-- Top border strip -->
				<img src="/images/marbleBorder.png" alt="" style="width: 100%; height: 20px; display: block; object-fit: cover;" />

				<div class="flex flex-1 flex-col justify-center px-10 py-10">

					<!-- Heading -->
					<h2 class="mb-6 text-2xl font-semibold" style="color: var(--color-charcoal); font-family: var(--font-family-display); letter-spacing: 0.03em;">
						<span ro-i18n eikey="login.memberAccess">Member Access</span>
					</h2>

					@if (logoutReason) {
						<div class="mb-4 rounded border px-4 py-3 text-sm"
							style="border-color: var(--color-gold-500); background-color: rgba(180,138,74,0.08); color: var(--color-charcoal);">
							<span ro-i18n eikey="login.unauthorized">Your session expired. Please sign in again.</span>
						</div>
					}

					@if (invalidLogin) {
						<div class="mb-4 rounded border px-4 py-3 text-sm"
							style="border-color: var(--color-terra-500); background-color: rgba(195,90,60,0.08); color: var(--color-terra-600);">
							<span ro-i18n eikey="login.invalidLogin">Incorrect username or password.</span>
						</div>
					}

					<form #loginForm="ngForm" (submit)="onSubmit(loginForm.value)" novalidate class="space-y-4">

						<!-- Email -->
						<div>
							<label for="id_username" class="mb-1 block text-xs font-medium tracking-wide"
								style="color: var(--color-charcoal); font-family: var(--font-family-ui);">
								<span ro-i18n eikey="general.email">Email</span>
							</label>
							<input
								id="id_username"
								name="uname"
								type="text"
								class="w-full rounded border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
								style="border-color: var(--color-sandstone); background-color: var(--color-marble); color: var(--color-charcoal); font-family: var(--font-family-ui); --tw-ring-color: var(--color-aegean-400);"
								placeholder="{{ getMessage('general.email') }}"
								autocomplete="username"
								[(ngModel)]="username"
								required
							/>
						</div>

						<!-- Password -->
						<div>
							<label for="id_password" class="mb-1 block text-xs font-medium tracking-wide"
								style="color: var(--color-charcoal); font-family: var(--font-family-ui);">
								<span ro-i18n eikey="general.password">Password</span>
							</label>
							<input
								id="id_password"
								name="pword"
								type="password"
								class="w-full rounded border px-3 py-2 text-sm outline-none transition-colors focus:ring-2"
								style="border-color: var(--color-sandstone); background-color: var(--color-marble); color: var(--color-charcoal); font-family: var(--font-family-ui); --tw-ring-color: var(--color-aegean-400);"
								placeholder="{{ getMessage('general.password') }}"
								autocomplete="current-password"
								[(ngModel)]="password"
								required
							/>
						</div>

						<!-- Submit -->
						<button
							type="submit"
							[disabled]="!loginForm.valid || submitting()"
							class="mt-2 w-full rounded px-4 py-2.5 text-sm font-semibold tracking-wide shadow transition-colors disabled:cursor-not-allowed disabled:opacity-50"
							style="background-color: var(--color-aegean-600); color: var(--color-marble); font-family: var(--font-family-display); letter-spacing: 0.06em;">
							@if (submitting()) {
								<span class="flex items-center justify-center gap-2">
									<svg class="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
										<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
										<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
									</svg>
									<span ro-i18n eikey="general.loading">Signing in…</span>
								</span>
							} @else {
								<span ro-i18n eikey="login.buttonText">Enter the Forum</span>
							}
						</button>

						<!-- Sign up + forgot row -->
						<div class="flex items-center justify-between pt-1">
							<button
								type="button"
								(click)="router.navigate(['/signup'])"
								class="text-xs transition-colors"
								style="color: var(--color-aegean-600); font-family: var(--font-family-ui);">
								<span ro-i18n eikey="login.signUp">Sign up</span>
							</button>
							<button
								type="button"
								(click)="forgotPassword(loginForm)"
								class="text-xs transition-colors"
								style="color: var(--color-aegean-600); font-family: var(--font-family-ui);">
								<span ro-i18n eikey="login.spacedPwd">Forgot password?</span>
							</button>
						</div>

					</form>

					<!-- Bottom tagline -->
					<p class="mt-8 text-center text-xs" style="color: var(--color-aegean-600); font-family: var(--font-family-ui);">
						<span ro-i18n eikey="login.tagline">Participation favors deliberation over speed.</span>
					</p>

				</div>

				<!-- Bottom border strip -->
				<img src="/images/marbleBorder.png" alt="" style="width: 100%; height: 20px; display: block; object-fit: cover;" />

			</div>
		</div>

	</div>
</div>
